/**
 * Authorization status values and helper functions for medical PA orders.
 * @module medicalPaOrdersAuthStatus
 */

import { Colors } from "risa-oasis-ui_v2";
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { BadgeWithIconProps } from "../data-model/badgeWithIconProps";

/** All possible authorization statuses for a medical PA order. */
export enum MedicalPaOrdersAuthStatus {
  NotToWork_Fedora = "not_to_work_fedora",
  NotToWork_Stat = "not_to_work_stat",
  WorkedByOnsite = "worked_by_onsite",
  NotAvailable = "not_available",
  Query = "query",
  Hold = "hold",
  AuthRequired = "auth_required",
  RisaDoNotWork = "risa_do_not_work",
  Pending = "pending",
  DeniedByRISA = "denied_by_risa",
  DenialByRISA = "denial_by_risa",
  ExistingDenial = "existing_denial",
  AuthByRISA = "auth_by_risa",
  NoAuthRequired = "no_auth_required",
  AuthOnFile = "auth_on_file",
  POD = "pod",
  NotApplicable = "not_applicable",
  TreatmentPlanChanged = "treatment_plan_changed",
  DrugChanged = "drug_changed",
  DrugRemoved = "drug_removed",
  DosChanged = "dos_changed",
  NotToWork_OralDrug = "not_to_work_oral_drug",
  Oral_Drug = "oral_drug",
  POI = "poi",
  AuthReview = "auth_review",
  DenialAfterQuery = "denial_after_query",
  QueryResolved = "query_resolved",
  New = "new",
}

/**
 * Finds the auth status ID matching the given display text.
 *
 * @param statusText - Human-readable auth status label.
 * @param authStatusOptions - Available auth status options to search.
 * @returns The matching status ID, or undefined if not found.
 */
export const getAuthStatusIdFromText = (
  statusText: string,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return authStatusOptions.find((status) => status.text === statusText)?.id;
};

/**
 * Determines the auth status with the lowest priority value from a list of status IDs.
 * Lower numeric priority = higher precedence; used to pick the "winning" status.
 *
 * @param statusIds - Candidate auth status IDs to evaluate.
 * @param authStatusOptions - Full list of configured auth status options with priorities.
 * @returns The ID of the status with the lowest priority, or undefined if none match.
 */
export const getLeastPriorityAuthStatusId = (
  statusIds: string[],
  authStatusOptions: AuthStatusOptionModel[],
): string | undefined => {
  const matchingStatuses = authStatusOptions.filter((status) =>
    statusIds.includes(status.id),
  );

  if (matchingStatuses.length === 0) {
    return undefined;
  }

  const leastPriorityStatus = matchingStatuses.reduce((current, next) => {
    const currentPriority = current.priority ?? Number.MAX_SAFE_INTEGER;
    const nextPriority = next.priority ?? Number.MAX_SAFE_INTEGER;

    return nextPriority < currentPriority ? next : current;
  });

  return leastPriorityStatus.id;
};

/**
 * Resolves badge display props (colors, text) for a given auth status.
 *
 * Fallback behavior:
 * - When `status` is undefined/empty: returns a hardcoded "Not Available"
 *   badge (gray background, black text). This is intentionally independent of
 *   `authStatusOptions` so the UI renders a consistent default even if the
 *   configured options list does not contain `NotAvailable`.
 * - When `status` is provided but does not match any option: returns a generic
 *   hardcoded "New" badge (blue).
 *
 * @param authStatusOptions - Configured auth status options with visual styling.
 * @param status - Current auth status ID to look up.
 * @returns Badge props for rendering. In practice always defined; the
 *   `| undefined` in the return type is retained for backward compatibility
 *   with callers that already handle the nullable case.
 */
export const getBadgePropsForAuthStatus = (
  authStatusOptions: AuthStatusOptionModel[],
  status?: string,
): BadgeWithIconProps | undefined => {
  const defaultBadgeProps = {
    id: MedicalPaOrdersAuthStatus.NotAvailable,
    text: "Not Available",
    bgColor: Colors.primaryGray16, // #F5F5F5
    textColor: Colors.grayscaleBlack1, // #0F0F0F
  } as BadgeWithIconProps;
  if (!status) {
    return defaultBadgeProps;
  }

  const statusToDisplay = authStatusOptions.find(
    (option) => option.id === status,
  );

  return (
    statusToDisplay ??
    ({
      id: MedicalPaOrdersAuthStatus.New,
      text: "New",
      bgColor: Colors.tertiaryBlue12,
      textColor: Colors.tertiaryBlue4,
    } as BadgeWithIconProps)
  );
};
